import { ChevronsRight, History, Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import type { Dispatch } from 'react';
import type { ScrubAction, ScrubState } from '../replayState';

/**
 * The replay/history transport (WS-17b, extracted for WS-17c). Lifted out of
 * `GameBoardPage` verbatim when Battleship arrived, because the scrub controls were never
 * Connect-4-specific: they drive `replayState.ts`'s reducer over frames that
 * `replayStates(moves, derive)` produced, and every game kind supplies its own `derive`.
 * Both boards now render the identical control strip, which is also the point — replay
 * should feel like one feature of "games", not two implementations that drifted.
 *
 * Purely presentational: it dispatches actions and never writes to a doc.
 */
export function ReplayControls({
  scrub,
  dispatch,
  maxFrame,
  finished,
  positionLabel,
}: {
  scrub: ScrubState;
  dispatch: Dispatch<ScrubAction>;
  maxFrame: number;
  finished: boolean;
  /** e.g. "Move 4 of 11 · 3 Aug, 14:02" — the kind supplies its own wording. */
  positionLabel: string;
}): JSX.Element {
  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-line bg-raised p-3"
      data-testid="replay-controls"
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-fg-3"
          data-testid="replay-badge"
        >
          <History size={13} aria-hidden="true" />
          {finished ? 'Replay' : 'Viewing history'}
        </span>
        <button
          type="button"
          data-testid="back-to-live-button"
          onClick={() => dispatch({ type: 'back-to-live' })}
          className="flex h-9 items-center rounded-full border border-line px-3 text-xs font-semibold text-fg-2"
        >
          {finished ? 'Exit replay' : 'Back to live'}
        </button>
      </div>

      {scrub.newMoveAvailable && (
        <button
          type="button"
          data-testid="new-move-chip"
          onClick={() => dispatch({ type: 'back-to-live' })}
          className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-inverse text-xs font-semibold text-inverse-fg"
        >
          Game moved on — back to live?
        </button>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Step back"
          data-testid="replay-step-back"
          disabled={scrub.frame <= 0}
          onClick={() => dispatch({ type: 'step', delta: -1, maxFrame })}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
        >
          <SkipBack size={16} />
        </button>
        {scrub.playing ? (
          <button
            type="button"
            aria-label="Pause"
            data-testid="replay-pause"
            onClick={() => dispatch({ type: 'pause' })}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-inverse text-inverse-fg"
          >
            <Pause size={16} />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Play"
            data-testid="replay-play"
            disabled={scrub.frame >= maxFrame}
            onClick={() => dispatch({ type: 'play' })}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-inverse text-inverse-fg disabled:opacity-40"
          >
            <Play size={16} />
          </button>
        )}
        <button
          type="button"
          aria-label="Step forward"
          data-testid="replay-step-forward"
          disabled={scrub.frame >= maxFrame}
          onClick={() => dispatch({ type: 'step', delta: 1, maxFrame })}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
        >
          <SkipForward size={16} />
        </button>
        <input
          type="range"
          aria-label="Scrub to move"
          data-testid="replay-scrubber"
          min={0}
          max={maxFrame}
          value={scrub.frame}
          onChange={(e) => dispatch({ type: 'scrub-to', frame: Number(e.target.value) })}
          className="h-11 flex-1 accent-fg"
        />
        <button
          type="button"
          aria-label="Jump to end"
          data-testid="replay-jump-end"
          disabled={scrub.frame >= maxFrame}
          onClick={() => dispatch({ type: 'jump-to-end', frame: maxFrame })}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
        >
          <ChevronsRight size={16} />
        </button>
      </div>

      <p className="text-center text-xs text-fg-3" data-testid="replay-position">
        {positionLabel}
      </p>
    </div>
  );
}

/** The header's "Watch replay" / "History" affordance — same pair on every game kind. */
export function ReplayEntryButtons({
  canWatchReplay,
  canSeeHistory,
  onWatchReplay,
  onSeeHistory,
}: {
  canWatchReplay: boolean;
  canSeeHistory: boolean;
  onWatchReplay: () => void;
  onSeeHistory: () => void;
}): JSX.Element {
  return (
    <>
      {canWatchReplay && (
        <button
          type="button"
          data-testid="watch-replay-button"
          aria-label="Watch replay"
          onClick={onWatchReplay}
          className="flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-line-strong px-3 text-sm font-semibold text-fg"
        >
          <Play size={15} aria-hidden="true" /> Watch replay
        </button>
      )}
      {canSeeHistory && (
        <button
          type="button"
          data-testid="see-history-button"
          aria-label="See how it went"
          onClick={onSeeHistory}
          className="flex h-11 shrink-0 items-center gap-1.5 rounded-full px-2 text-xs font-medium text-fg-3"
        >
          <History size={14} aria-hidden="true" /> History
        </button>
      )}
    </>
  );
}
